import { HStack, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { selectCoordinatesUnit } from '@redux/settings/settings.selectors';
import { usePatchCoordinatesUnitMutation } from '@services/config/setting.api';
import { formatCoordinates } from '@utils/validation/coordinates.utils';
import DmsCoordinates from 'dms-conversion';
import { useCallback } from 'react';

import { Coordinates, CoordinatesUnitEnum } from '@/types/commons/commons.types';

import PopupContainer from './PopupContainer';

type Props = {
  coordinates: Coordinates;
  display?: 'vertical' | 'horizontal';
  prefix?: string;
};

export default function CoordinatesInformation({ coordinates, display = 'horizontal', prefix }: Readonly<Props>) {
  const userSettingsUnit = useAppSelector(selectCoordinatesUnit);
  const { login } = useAppSelector(selectActiveUser);

  const [updateCoordinatesUnit] = usePatchCoordinatesUnitMutation();
  const coordinatesDMS = new DmsCoordinates(coordinates.latitude, coordinates.longitude);
  const formattedCoordinates = formatCoordinates(coordinatesDMS, userSettingsUnit);
  const unitValues = Object.values(CoordinatesUnitEnum);
  const currentIndex = unitValues.indexOf(userSettingsUnit);
  const handleCoordinatesClick = useCallback(() => {
    const nextIndex = (currentIndex + 1) % unitValues.length;
    const nextUnit = unitValues[nextIndex];
    updateCoordinatesUnit({ login, coordinatesUnit: nextUnit });
  }, [currentIndex, unitValues, updateCoordinatesUnit, login]);

  if (display === 'vertical') {
    return (
      <PopupContainer onClick={handleCoordinatesClick}>
        <VStack gap="tightM" textStyle="screenStd" alignItems="start">
          <Text>{formattedCoordinates.latitude}</Text>
          <Text>{formattedCoordinates.longitude}</Text>
        </VStack>
      </PopupContainer>
    );
  }

  return (
    <PopupContainer onClick={handleCoordinatesClick}>
      <HStack gap="standardL" textStyle="screenStd">
        {prefix && <Text color="neutral.500">{prefix}</Text>}
        <Text>{formattedCoordinates.latitude}</Text>
        <Text>{formattedCoordinates.longitude}</Text>
      </HStack>
    </PopupContainer>
  );
}
